/**
 * Helles oder dunkles Thema — dem Gerät folgen, oder ausdrücklich wählen.
 *
 * Die Farben selbst stehen in `index.css` als `light-dark(hell, dunkel)`. Hier
 * wird nur entschieden, **welcher Zweig gilt**: über `color-scheme` am
 * `html`-Element, gesteuert durch das Attribut `data-theme`.
 *
 * Die Vorgabe ist „dem Gerät folgen". Wer im Gym nachts trainiert, hat sein
 * Telefon längst dunkel gestellt; eine App, die trotzdem hell leuchtet, ist kein
 * Standpunkt, sondern ein Fehler.
 *
 * Angewendet wird **vor dem ersten Rendern** (siehe `main.tsx`). Ein Umschalten
 * erst nach dem Zeichnen wäre ein sichtbares Aufblitzen der falschen Farbe.
 */

export const THEME_PREFERENCES = ['system', 'light', 'dark'] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];

const STORAGE_KEY = 'kraftwerk.theme';

function isPreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (THEME_PREFERENCES as readonly string[]).includes(value);
}

/**
 * Die gespeicherte Wahl — oder „dem Gerät folgen".
 *
 * `localStorage` kann werfen (privater Modus, gesperrter Speicher). Das darf die
 * App nicht anhalten: ohne lesbare Wahl gilt eben die Vorgabe.
 */
export function readThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isPreference(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

/** Schreibt die Wahl an das Dokument. Ohne Speichern — das macht `saveTheme`. */
export function applyTheme(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', preference);
}

/** Wahl merken und anwenden. Scheitert das Merken, gilt sie wenigstens jetzt. */
export function saveThemePreference(preference: ThemePreference): void {
  applyTheme(preference);
  try {
    if (preference === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Kein Speicher — die Wahl gilt für diese Sitzung. Nichts geht verloren,
    // was nicht ohnehin nur eine Anzeigeeinstellung wäre.
  }
}

/** Beim Start aufrufen, bevor gerendert wird. */
export function initTheme(): ThemePreference {
  const preference = readThemePreference();
  applyTheme(preference);
  return preference;
}
